import { z } from 'zod';
import {
  compararPercentual,
  somarDinheiro,
  somarPercentual,
  subtrairDinheiro,
} from './aritmetica.ts';
import { dataSchema, dinheiroSchema, uuidSchema } from './comum.ts';
import { ratearValorPorIndice } from './relatorio-contas-pagar.ts';
import { situacaoDoTitulo, situacaoTituloSchema } from './titulos.ts';

/**
 * Relatório de contas a receber (`REC·03`).
 *
 * O espelho do `PAG·03` do outro lado do caixa: os títulos a receber
 * **quebrados por empresa** e em ordem de **vencimento**, com subtotal de valor e
 * de saldo a cada data de vencimento — "o que entra em cada dia, e quanto disso
 * ainda falta receber", impresso. A listagem (`REC·01`) responde título a título,
 * paginada e sem soma; o painel (`REC·02`), de relance.
 *
 * ## De onde vem cada linha
 *
 * De `contas_receber`, e só dela: um título, uma linha. As três origens entram —
 * geração do contrato, geração da obra e lançamento manual (o aditivo incluído) —,
 * porque a pergunta é sobre o crédito, e não sobre quem o escreveu.
 *
 * ## As colunas que não são do título
 *
 * - **Emissão.** `contas_receber` não tem data de emissão: tem `data_lancamento`.
 *   Ao contrário do pagar, aqui **não há documento fiscal** a consultar — nenhuma
 *   nota de saída gera título a receber —, e as duas gerações gravam em
 *   `data_lancamento` a data do documento de origem (a do contrato, a da obra).
 *   A emissão é, então, o próprio lançamento, e o filtro lê a mesma coluna.
 * - **Obra / contrato.** O contrato é chave do título (`contrato_id`), um só ou
 *   nenhum. A obra, não: é o **rateio** (`contas_receber_rateio`), que pode ter
 *   várias obras, a mesma obra em trechos diferentes e somar menos de 100% — a
 *   receita administrativa não é de obra nenhuma. A linha mostra a **obra
 *   principal** (a de maior índice somado; empate pelo nome) e quantas mais há
 *   ("+2"). O resumo por obra, ao contrário, **divide** valor e saldo pelo índice
 *   de cada obra, e o que o rateio não cobre vai para "Sem obra" — é o que faz o
 *   resumo fechar exatamente no total. Decisão do Adriano, 25/09/2026, a mesma da
 *   conta contábil do `PAG·03`.
 *
 *   `contas_receber.obra_id` **não** é lido: ele diz quem *gerou* o título, e não
 *   de qual obra é a receita — a decisão 22, item 4. A geração da obra grava o
 *   rateio de 100% na própria obra, então o título dela aparece do mesmo jeito.
 *
 * ## O que se agrega onde
 *
 * O servidor devolve empresas, vencimentos e resumo **já montados** — dinheiro se
 * soma aqui, com `BigInt`. A tela só decide se mostra o resumo.
 */

/* -------------------------------------------------------------------------- */
/* Teto                                                                       */
/* -------------------------------------------------------------------------- */

/**
 * Teto de títulos por consulta. O relatório não pagina e **recusa** acima dele
 * em vez de truncar — o mesmo teto e a mesma razão do `PAG·03`.
 */
export const LIMITE_TITULOS_RELATORIO_CONTAS_RECEBER = 5000;

/* -------------------------------------------------------------------------- */
/* Consulta                                                                   */
/* -------------------------------------------------------------------------- */

/**
 * Filtros do relatório. **Todos opcionais**, e a ausência é "sem recorte".
 *
 * - `empresaId` é a empresa **credora** (`contas_receber.empresa_id`).
 * - Os dois períodos são inclusivos nas duas pontas. A emissão é o lançamento
 *   do título (ver o cabeçalho).
 * - `situacao` segue o **recorte da listagem**: `EM_ABERTO` traz tudo com saldo,
 *   inclusive o recebido em parte; `PARCIAL` só esses; `QUITADO` é saldo zero. É
 *   o mesmo `filtroDeSituacao` do repositório do contas a receber.
 *
 * Estrito: parâmetro desconhecido é erro, e não silêncio (ver `query-estrita`).
 */
export const relatorioContasReceberQuerySchema = z
  .object({
    empresaId: uuidSchema.optional(),
    clienteId: uuidSchema.optional(),
    emissaoDe: dataSchema.optional(),
    emissaoAte: dataSchema.optional(),
    vencimentoDe: dataSchema.optional(),
    vencimentoAte: dataSchema.optional(),
    situacao: situacaoTituloSchema.optional(),
  })
  .strict();

export type RelatorioContasReceberQuery = z.infer<typeof relatorioContasReceberQuerySchema>;
/** O que o cliente manda. Igual à saída hoje, e separado por simetria com o PAG·03. */
export type RelatorioContasReceberQueryEntrada = z.input<typeof relatorioContasReceberQuerySchema>;

/* -------------------------------------------------------------------------- */
/* Resposta                                                                   */
/* -------------------------------------------------------------------------- */

/** Um título do relatório. */
export const linhaRelatorioContasReceberSchema = z.object({
  contaReceberId: uuidSchema,
  numeroDocumento: z.string(),
  parcela: z.number().int().positive(),
  totalParcelas: z.number().int().positive(),
  /** O lançamento do título. */
  dataEmissao: dataSchema,
  dataVencimento: dataSchema,
  valor: dinheiroSchema,
  valorSaldo: dinheiroSchema,
  situacao: situacaoTituloSchema,
  clienteId: uuidSchema,
  clienteNome: z.string(),
  /**
   * O contrato do título: o que o título aponta (parcela gerada pelo contrato
   * ou aditivo digitado) ou, no título de **nota de serviço**, o que a nota
   * cita — o título de nota nasce sem `contrato_id` de propósito (`.spec/015`,
   * decisões 90 e 96). A nota que aponta só a obra sai sem contrato, ainda
   * que a obra tenha um: o relatório mostra o que o documento diz.
   */
  contratoId: uuidSchema.nullable(),
  contratoNumero: z.string().nullable(),
  /** A obra de maior índice no rateio. `null` no título sem rateio de obra. */
  obraId: uuidSchema.nullable(),
  obraNome: z.string().nullable(),
  /** Quantas obras o rateio tem **além** da principal — o "+2" da coluna. */
  outrasObras: z.number().int().nonnegative(),
});

export type LinhaRelatorioContasReceber = z.infer<typeof linhaRelatorioContasReceberSchema>;

/** Soma de um bloco: um vencimento, uma empresa ou o relatório inteiro. */
export const totaisRelatorioContasReceberSchema = z.object({
  valor: dinheiroSchema,
  saldo: dinheiroSchema,
  titulos: z.number().int().nonnegative(),
});

export type TotaisRelatorioContasReceber = z.infer<typeof totaisRelatorioContasReceberSchema>;

/** Os títulos de uma data de vencimento, com o subtotal dela. */
export const vencimentoRelatorioContasReceberSchema = z.object({
  dataVencimento: dataSchema,
  titulos: z.array(linhaRelatorioContasReceberSchema),
  totais: totaisRelatorioContasReceberSchema,
});

export type VencimentoRelatorioContasReceber = z.infer<
  typeof vencimentoRelatorioContasReceberSchema
>;

/** A quebra por empresa. */
export const empresaRelatorioContasReceberSchema = z.object({
  empresaId: uuidSchema,
  empresaNome: z.string(),
  vencimentos: z.array(vencimentoRelatorioContasReceberSchema),
  totais: totaisRelatorioContasReceberSchema,
});

export type EmpresaRelatorioContasReceber = z.infer<typeof empresaRelatorioContasReceberSchema>;

/** Uma linha do resumo por cliente: o título inteiro vai ao cliente dele. */
export const clienteResumoContasReceberSchema = z.object({
  clienteId: uuidSchema,
  clienteNome: z.string(),
  valor: dinheiroSchema,
  saldo: dinheiroSchema,
  titulos: z.number().int().nonnegative(),
});

export type ClienteResumoContasReceber = z.infer<typeof clienteResumoContasReceberSchema>;

/**
 * Uma linha do resumo por obra — valor e saldo **rateados** pelo índice de cada
 * título. `titulos` conta os títulos que têm fatia na obra; a soma da coluna
 * passa do total quando algum título tem mais de uma obra, ou parte sem obra.
 */
export const obraResumoContasReceberSchema = z.object({
  /** `null` é a linha "Sem obra": o título sem rateio e a parte que o rateio não cobre. */
  obraId: uuidSchema.nullable(),
  obraNome: z.string().nullable(),
  valor: dinheiroSchema,
  saldo: dinheiroSchema,
  titulos: z.number().int().nonnegative(),
});

export type ObraResumoContasReceber = z.infer<typeof obraResumoContasReceberSchema>;

export const resumoRelatorioContasReceberSchema = z.object({
  porCliente: z.array(clienteResumoContasReceberSchema),
  porObra: z.array(obraResumoContasReceberSchema),
});

export type ResumoRelatorioContasReceber = z.infer<typeof resumoRelatorioContasReceberSchema>;

/**
 * O relatório inteiro.
 *
 * O filtro volta **resolvido** — os nomes da empresa e do cliente —, porque o PDF
 * imprime o cabeçalho. As datas voltam como vieram, `null` incluído. Só aparecem
 * as empresas **com** título no recorte.
 */
export const relatorioContasReceberSchema = z.object({
  empresaId: uuidSchema.nullable(),
  empresaNome: z.string().nullable(),
  clienteId: uuidSchema.nullable(),
  clienteNome: z.string().nullable(),
  emissaoDe: dataSchema.nullable(),
  emissaoAte: dataSchema.nullable(),
  vencimentoDe: dataSchema.nullable(),
  vencimentoAte: dataSchema.nullable(),
  situacao: situacaoTituloSchema.nullable(),
  empresas: z.array(empresaRelatorioContasReceberSchema),
  totais: totaisRelatorioContasReceberSchema,
  resumo: resumoRelatorioContasReceberSchema,
});

export type RelatorioContasReceber = z.infer<typeof relatorioContasReceberSchema>;

/* -------------------------------------------------------------------------- */
/* Montagem — do que o banco leu ao relatório                                 */
/* -------------------------------------------------------------------------- */

/** Um título como o repositório o devolve, com empresa, cliente e contrato ao lado. */
export interface TituloBrutoContasReceber {
  contaReceberId: string;
  empresaId: string;
  empresaNome: string;
  clienteId: string;
  clienteNome: string;
  contratoId: string | null;
  contratoNumero: string | null;
  numeroDocumento: string;
  parcela: number;
  totalParcelas: number;
  dataEmissao: string;
  dataVencimento: string;
  valor: string;
  valorSaldo: string;
}

/**
 * Uma fatia do rateio de obra de um título, como a tabela a guarda: uma por
 * obra **e trecho** — a mesma obra pode aparecer mais de uma vez.
 */
export interface RateioBrutoContasReceber {
  contaReceberId: string;
  obraId: string;
  obraNome: string;
  indiceRateio: string;
}

/** A obra do título com o índice dos trechos dela já somado. */
export interface ObraDoTitulo {
  obraId: string;
  obraNome: string;
  indiceRateio: string;
}

const COMPARAR_TEXTO = new Intl.Collator('pt-BR', { sensitivity: 'base', numeric: true });

/**
 * As obras de um título, **uma por obra**: os trechos da mesma obra somam o
 * índice. Sem isso, a obra rateada em três trechos de 20% perderia a principal
 * para outra de 40%, tendo 60% do título.
 */
export function obrasDoTitulo(fatias: readonly RateioBrutoContasReceber[]): ObraDoTitulo[] {
  const porObra = new Map<string, { obraNome: string; indices: string[] }>();
  for (const fatia of fatias) {
    const atual = porObra.get(fatia.obraId) ?? { obraNome: fatia.obraNome, indices: [] };
    atual.indices.push(fatia.indiceRateio);
    porObra.set(fatia.obraId, atual);
  }

  return [...porObra].map(([obraId, { obraNome, indices }]) => ({
    obraId,
    obraNome,
    indiceRateio: somarPercentual(indices),
  }));
}

/**
 * A obra que representa o título na linha: a de **maior índice**.
 *
 * Empate de índice — 50% e 50% — desempata pelo nome, para o mesmo título sair
 * sempre com a mesma obra, na tela e no PDF.
 */
export function obraPrincipalDoTitulo(obras: readonly ObraDoTitulo[]): ObraDoTitulo | null {
  const [principal = null] = [...obras].sort(
    (a, b) =>
      compararPercentual(b.indiceRateio, a.indiceRateio) ||
      COMPARAR_TEXTO.compare(a.obraNome, b.obraNome) ||
      a.obraId.localeCompare(b.obraId),
  );
  return principal;
}

function totaisDe(
  titulos: readonly { valor: string; valorSaldo: string }[],
): TotaisRelatorioContasReceber {
  return {
    valor: somarDinheiro(titulos.map((t) => t.valor)),
    saldo: somarDinheiro(titulos.map((t) => t.valorSaldo)),
    titulos: titulos.length,
  };
}

function paraLinha(
  titulo: TituloBrutoContasReceber,
  obras: readonly ObraDoTitulo[],
): LinhaRelatorioContasReceber {
  const principal = obraPrincipalDoTitulo(obras);

  return {
    contaReceberId: titulo.contaReceberId,
    numeroDocumento: titulo.numeroDocumento,
    parcela: titulo.parcela,
    totalParcelas: titulo.totalParcelas,
    dataEmissao: titulo.dataEmissao,
    dataVencimento: titulo.dataVencimento,
    valor: titulo.valor,
    valorSaldo: titulo.valorSaldo,
    situacao: situacaoDoTitulo(titulo.valor, titulo.valorSaldo),
    clienteId: titulo.clienteId,
    clienteNome: titulo.clienteNome,
    contratoId: titulo.contratoId,
    contratoNumero: titulo.contratoNumero,
    obraId: principal?.obraId ?? null,
    obraNome: principal?.obraNome ?? null,
    outrasObras: Math.max(obras.length - 1, 0),
  };
}

/**
 * A ordem dentro de um vencimento: cliente, documento e parcela.
 *
 * O vencimento já é a quebra; dentro dele, o cliente agrupa o que se cobra junto,
 * e o documento com a parcela deixa as parcelas de um contrato em sequência.
 */
function compararTitulos(a: LinhaRelatorioContasReceber, b: LinhaRelatorioContasReceber): number {
  return (
    COMPARAR_TEXTO.compare(a.clienteNome, b.clienteNome) ||
    COMPARAR_TEXTO.compare(a.numeroDocumento, b.numeroDocumento) ||
    a.parcela - b.parcela ||
    a.contaReceberId.localeCompare(b.contaReceberId)
  );
}

function vencimentosDe(
  linhas: readonly LinhaRelatorioContasReceber[],
): VencimentoRelatorioContasReceber[] {
  const porData = new Map<string, LinhaRelatorioContasReceber[]>();
  for (const linha of linhas) {
    const doDia = porData.get(linha.dataVencimento) ?? [];
    doDia.push(linha);
    porData.set(linha.dataVencimento, doDia);
  }

  return [...porData]
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([dataVencimento, titulos]) => {
      const ordenados = [...titulos].sort(compararTitulos);
      return { dataVencimento, titulos: ordenados, totais: totaisDe(ordenados) };
    });
}

interface Acumulado {
  valores: string[];
  saldos: string[];
  titulos: number;
}

function acumular(mapa: Map<string, Acumulado>, chave: string, valor: string, saldo: string): void {
  const atual = mapa.get(chave) ?? { valores: [], saldos: [], titulos: 0 };
  atual.valores.push(valor);
  atual.saldos.push(saldo);
  atual.titulos += 1;
  mapa.set(chave, atual);
}

/** O resumo por cliente: o título inteiro vai ao cliente dele. */
function resumoPorCliente(
  titulos: readonly TituloBrutoContasReceber[],
): ClienteResumoContasReceber[] {
  const acumulados = new Map<string, Acumulado>();
  const nomes = new Map<string, string>();

  for (const titulo of titulos) {
    acumular(acumulados, titulo.clienteId, titulo.valor, titulo.valorSaldo);
    nomes.set(titulo.clienteId, titulo.clienteNome);
  }

  return [...acumulados]
    .map(([clienteId, a]) => ({
      clienteId,
      clienteNome: nomes.get(clienteId) ?? '',
      valor: somarDinheiro(a.valores),
      saldo: somarDinheiro(a.saldos),
      titulos: a.titulos,
    }))
    .sort((a, b) => COMPARAR_TEXTO.compare(a.clienteNome, b.clienteNome));
}

const SEM_OBRA = '';

function ehZero(valor: string): boolean {
  return /^-?0+(\.0+)?$/.test(valor);
}

/**
 * O resumo por obra: valor e saldo de cada título **divididos** pelo índice de
 * cada obra, com `ratearValorPorIndice` (maior resto, sem perder centavo).
 *
 * O que o rateio não cobre — o título sem rateio, ou a parte de um rateio que
 * soma menos de 100% — vai para a linha "Sem obra", calculada como **o que
 * sobrou** (valor menos as fatias), e não por uma segunda conta de percentual:
 * é isso que garante que o resumo fecha no total do relatório.
 *
 * Valor e saldo são rateados cada um por si — derivar o saldo pela proporção do
 * valor arredondaria duas vezes.
 */
function resumoPorObra(
  titulos: readonly TituloBrutoContasReceber[],
  obrasPorTitulo: ReadonlyMap<string, readonly ObraDoTitulo[]>,
): ObraResumoContasReceber[] {
  const acumulados = new Map<string, Acumulado>();
  const nomes = new Map<string, string | null>();

  for (const titulo of titulos) {
    const obras = obrasPorTitulo.get(titulo.contaReceberId) ?? [];
    const indices = obras.map((obra) => obra.indiceRateio);
    const valores = ratearValorPorIndice(titulo.valor, indices);
    const saldos = ratearValorPorIndice(titulo.valorSaldo, indices);

    obras.forEach((obra, indice) => {
      acumular(acumulados, obra.obraId, valores[indice] as string, saldos[indice] as string);
      nomes.set(obra.obraId, obra.obraNome);
    });

    const restoValor = subtrairDinheiro(titulo.valor, somarDinheiro(valores));
    const restoSaldo = subtrairDinheiro(titulo.valorSaldo, somarDinheiro(saldos));
    if (obras.length === 0 || !ehZero(restoValor) || !ehZero(restoSaldo)) {
      acumular(acumulados, SEM_OBRA, restoValor, restoSaldo);
      nomes.set(SEM_OBRA, null);
    }
  }

  return [...acumulados]
    .map(([chave, a]) => ({
      obraId: chave === SEM_OBRA ? null : chave,
      obraNome: nomes.get(chave) ?? null,
      valor: somarDinheiro(a.valores),
      saldo: somarDinheiro(a.saldos),
      titulos: a.titulos,
    }))
    .sort(
      (a, b) =>
        (a.obraId === null ? 1 : 0) - (b.obraId === null ? 1 : 0) ||
        COMPARAR_TEXTO.compare(a.obraNome ?? '', b.obraNome ?? ''),
    );
}

/**
 * Monta o relatório: quebra por empresa, agrupa por vencimento, fecha os
 * subtotais e o resumo.
 *
 * Vive no contrato, e não no service, porque é regra pura e testável sem banco:
 * as somas e a divisão por obra são o ponto em que o relatório pode perder um
 * centavo, e é aí que o teste precisa estar.
 *
 * Empresas em ordem alfabética; vencimentos do mais antigo ao mais novo.
 */
export function montarRelatorioContasReceber(
  titulos: readonly TituloBrutoContasReceber[],
  rateios: readonly RateioBrutoContasReceber[],
): Pick<RelatorioContasReceber, 'empresas' | 'totais' | 'resumo'> {
  const fatiasPorTitulo = new Map<string, RateioBrutoContasReceber[]>();
  for (const fatia of rateios) {
    const doTitulo = fatiasPorTitulo.get(fatia.contaReceberId) ?? [];
    doTitulo.push(fatia);
    fatiasPorTitulo.set(fatia.contaReceberId, doTitulo);
  }

  const obrasPorTitulo = new Map<string, ObraDoTitulo[]>();
  for (const [contaReceberId, fatias] of fatiasPorTitulo) {
    obrasPorTitulo.set(contaReceberId, obrasDoTitulo(fatias));
  }

  const porEmpresa = new Map<string, { nome: string; linhas: LinhaRelatorioContasReceber[] }>();
  for (const titulo of titulos) {
    const empresa = porEmpresa.get(titulo.empresaId) ?? { nome: titulo.empresaNome, linhas: [] };
    empresa.linhas.push(paraLinha(titulo, obrasPorTitulo.get(titulo.contaReceberId) ?? []));
    porEmpresa.set(titulo.empresaId, empresa);
  }

  const empresas = [...porEmpresa]
    .map(([empresaId, { nome, linhas }]) => ({
      empresaId,
      empresaNome: nome,
      vencimentos: vencimentosDe(linhas),
      totais: totaisDe(linhas),
    }))
    .sort((a, b) => COMPARAR_TEXTO.compare(a.empresaNome, b.empresaNome));

  return {
    empresas,
    totais: totaisDe(titulos),
    resumo: {
      porCliente: resumoPorCliente(titulos),
      porObra: resumoPorObra(titulos, obrasPorTitulo),
    },
  };
}
